import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiCheck, FiCopy, FiDownload } from 'react-icons/fi';
import { Button, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import {
  fetchAgentsForAgency,
  fetchSingleAgent,
  getPreUnderwritingDraft,
  savePreUnderwritingDraft,
  sendPreUnderwritingLogEmail,
  submitPreUnderwritingLog,
} from '../../lib/preUnderwritingPublicApi';
import {
  FORM_STEPS,
  FORM_STEPS_NO_PICKER,
  buildSubmitPayload,
  collectSubmitValidationErrors,
  formatSubmitValidationError,
  hydrateFormFromDraft,
  initialForm,
  sanitizeFilePart,
} from './lib/fieldDefs';
import { blobToBase64, downloadBlob, generatePdfBlob } from './lib/pdfGenerator';
import Step0AgentPicker from './steps/Step0AgentPicker';
import Step1Disclosure from './steps/Step1Disclosure';
import Step2PersonalInfo from './steps/Step2PersonalInfo';
import Step3Financial from './steps/Step3Financial';
import Step4Insurance from './steps/Step4Insurance';
import Step5Bank from './steps/Step5Bank';
import Step6Illustration from './steps/Step6Illustration';
import Step7HealthPrimary from './steps/Step7HealthPrimary';
import Step8Spouse from './steps/Step8Spouse';
import Step9ChildrenRiders from './steps/Step9ChildrenRiders';
import Step10AgentComments from './steps/Step10AgentComments';
import Step11Review from './steps/Step11Review';

function buildResumeLink(agencySlug, agentId, draftId) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://go.prosperityleaders.net';
  const path = agentId
    ? `/preapp/${agencySlug}/${agentId}`
    : `/preapp/${agencySlug}`;
  return `${origin}${path}?resume=${encodeURIComponent(draftId)}`;
}

export default function PreUnderwritingForm() {
  const { agencySlug, agentId: agentIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const resumeId = searchParams.get('resume') || '';
  const skipPicker = Boolean(agentIdParam);

  const stepLabels = skipPicker ? FORM_STEPS_NO_PICKER : FORM_STEPS;

  const [stepIndex, setStepIndex] = useState(0);
  const [furthestStepReached, setFurthestStepReached] = useState(0);
  const [form, setForm] = useState(() => ({
    ...initialForm,
    __agent_id: agentIdParam || '',
    __split_agent_id: '',
  }));
  const [draftId, setDraftId] = useState(null);
  const [agency, setAgency] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [draftLoading, setDraftLoading] = useState(Boolean(resumeId));
  const [agentsError, setAgentsError] = useState('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(null);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState(null);

  const progress = useMemo(
    () => ((stepIndex + 1) / stepLabels.length) * 100,
    [stepIndex, stepLabels.length],
  );

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateBeneficiary = useCallback((index, key, value) => {
    setForm((prev) => {
      const next = [...prev.beneficiaries];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, beneficiaries: next };
    });
  }, []);

  const updateChild = useCallback((index, key, value) => {
    setForm((prev) => {
      const next = [...prev.children];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, children: next };
    });
  }, []);

  const applyAgentSelection = useCallback((agent, splitAgent = null) => {
    setSelectedAgent(agent);
    setForm((prev) => ({
      ...prev,
      __agent_id: agent?.id || '',
      __split_agent_id: splitAgent?.id || prev.__split_agent_id || '',
      agent_name_code: agent?.full_name
        ? prev.agent_name_code || agent.full_name
        : prev.agent_name_code,
      split_agent_name_code: splitAgent?.full_name
        ? splitAgent.full_name
        : prev.split_agent_name_code,
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!agencySlug) {
        setAgentsError('Missing agency.');
        setAgentsLoading(false);
        setDraftLoading(false);
        return;
      }

      setAgentsLoading(true);
      setAgentsError('');

      try {
        if (agentIdParam) {
          const result = await fetchSingleAgent(agencySlug, agentIdParam);
          if (cancelled) return;
          if (!result.ok) {
            setAgentsError(result.error || 'Unable to load agency/agent');
            setAgentsLoading(false);
            setDraftLoading(false);
            return;
          }
          setAgency(result.agency);
          setAgents(result.agents || []);
          applyAgentSelection(result.agent);
        } else {
          const result = await fetchAgentsForAgency(agencySlug);
          if (cancelled) return;
          if (!result.ok) {
            setAgentsError(result.error || 'Unable to load agency');
            setAgentsLoading(false);
            setDraftLoading(false);
            return;
          }
          setAgency(result.agency);
          setAgents(result.agents || []);
        }

        if (resumeId) {
          setDraftLoading(true);
          const draftResult = await getPreUnderwritingDraft({
            id: resumeId,
            agency_slug: agencySlug,
          });
          if (cancelled) return;
          if (!draftResult.ok) {
            setError(draftResult.error || 'Unable to load saved draft.');
          } else {
            const hydrated = hydrateFormFromDraft(draftResult.draft, { agentIdParam });
            setForm(hydrated);
            setDraftId(resumeId);
            setFurthestStepReached(stepLabels.length - 1);
          }
          setDraftLoading(false);
        } else {
          setDraftLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setAgentsError(err instanceof Error ? err.message : 'Failed to load');
          setDraftLoading(false);
        }
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [agencySlug, agentIdParam, applyAgentSelection, resumeId, stepLabels.length]);

  // After agents + draft load, sync selectedAgent from hydrated agent id
  useEffect(() => {
    if (!form.__agent_id || !agents.length) return;
    const agent = agents.find((a) => a.id === form.__agent_id);
    if (agent) setSelectedAgent(agent);
  }, [form.__agent_id, agents]);

  const nextStep = () => {
    setError('');
    setValidationErrors([]);
    const next = Math.min(stepIndex + 1, stepLabels.length - 1);
    setFurthestStepReached((f) => Math.max(f, next));
    setStepIndex(next);
  };

  const previousStep = () => {
    setError('');
    setValidationErrors([]);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const goToStep = (index) => {
    if (index < 0 || index > furthestStepReached) return;
    setError('');
    setValidationErrors([]);
    setStepIndex(index);
  };

  const jumpToValidationError = (err) => {
    const idx = typeof err.stepIndex === 'number' ? err.stepIndex : stepLabels.indexOf(err.step);
    if (idx < 0) return;
    setFurthestStepReached((f) => Math.max(f, idx));
    setStepIndex(idx);
    setValidationErrors([]);
    setError('');
  };

  const handleSaveDraft = async () => {
    setError('');
    setValidationErrors([]);
    if (!agency?.id || !form.__agent_id) {
      setError('Please select an agent before saving a draft.');
      return;
    }

    setSavingDraft(true);
    try {
      const payload = buildSubmitPayload(form, {
        agencyId: agency.id,
        agentId: form.__agent_id,
        splitAgentId: form.__split_agent_id || null,
      });
      if (draftId) payload.id = draftId;
      payload.agency_slug = agencySlug;
      payload.resume_agent_id = agentIdParam || form.__agent_id;

      const result = await savePreUnderwritingDraft(payload);
      const id = result?.id;
      if (!id) throw new Error('Draft save succeeded but no id was returned.');

      setDraftId(id);
      const resumeUrl = buildResumeLink(agencySlug, agentIdParam || form.__agent_id, id);
      setDraftSaved({
        id,
        resumeUrl,
        emailSent: Boolean(result.email_sent),
      });

      // Keep resume param in URL for subsequent saves / reloads
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('resume', id);
      setSearchParams(nextParams, { replace: true });
    } catch (err) {
      console.error('[PreUnderwritingForm] draft save failed:', err);
      setError('Could not save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCopyResumeLink = async () => {
    if (!draftSaved?.resumeUrl) return;
    try {
      await navigator.clipboard.writeText(draftSaved.resumeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setValidationErrors([]);
    if (!agency?.id || !form.__agent_id) {
      setError('Please select an agent before submitting.');
      return;
    }

    const errors = collectSubmitValidationErrors(form, stepLabels);
    if (errors.length) {
      setValidationErrors(errors);
      setError('Please complete the required fields listed below before submitting.');
      return;
    }

    setSubmitting(true);
    let pdfBlob = null;
    let emailOk = false;

    try {
      const payload = buildSubmitPayload(form, {
        agencyId: agency.id,
        agentId: form.__agent_id,
        splitAgentId: form.__split_agent_id || null,
      });
      if (draftId) payload.id = draftId;

      pdfBlob = await generatePdfBlob(payload);
      const submitted = await submitPreUnderwritingLog(payload);
      const logId = submitted?.id;

      if (!logId) {
        throw new Error('Submission succeeded but no log id was returned.');
      }

      const pdf_filename = `PreUnderwriting_${sanitizeFilePart(payload.full_name)}.pdf`;
      const pdf_base64 = await blobToBase64(pdfBlob);

      try {
        const emailResult = await sendPreUnderwritingLogEmail({
          log_id: logId,
          pdf_base64,
          pdf_filename,
        });
        emailOk = Boolean(emailResult?.success);
      } catch {
        emailOk = false;
      }

      setSuccess({
        logId,
        pdfBlob,
        pdfFilename: pdf_filename,
        emailOk,
        fullName: payload.full_name,
        agentName: selectedAgent?.full_name || form.agent_name_code,
      });
    } catch (err) {
      console.error('[PreUnderwritingForm] submit failed:', err);
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
            <FiCheck className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Application Submitted</h1>
          <p className="mt-3 text-sm text-gray-600">
            Thank you{success.fullName ? `, ${success.fullName}` : ''}. Your pre-underwriting
            application log has been saved
            {success.agentName ? ` for ${success.agentName}` : ''}.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            {success.emailOk
              ? 'A PDF copy was emailed to your agent.'
              : 'We could not confirm email delivery to the agent. You can still download your PDF below.'}
          </p>
          <Button
            className="mt-6"
            type="button"
            onClick={() => downloadBlob(success.pdfBlob, success.pdfFilename)}
          >
            <FiDownload className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        </div>
      </div>
    );
  }

  if (!agencySlug) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <p className="text-sm text-red-600">Invalid link — missing agency.</p>
      </div>
    );
  }

  if (agentsLoading || draftLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 text-gray-600">
        <Spinner size="lg" className="h-8 w-8 text-primary" />
        <p className="text-sm">{draftLoading ? 'Loading saved draft…' : 'Loading…'}</p>
      </div>
    );
  }

  if (agentsError && !agency) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Agency not found</h1>
          <p className="mt-2 text-sm text-gray-600">{agentsError}</p>
        </Card>
      </div>
    );
  }

  const currentLabel = stepLabels[stepIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Pre-Underwriting Application</h1>
                {agency?.name ? (
                  <p className="text-sm text-gray-500">{agency.name}</p>
                ) : null}
                {selectedAgent && skipPicker ? (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-secondary">
                      Agent: <strong>{selectedAgent.full_name}</strong>
                      {selectedAgent.title ? ` · ${selectedAgent.title}` : ''}
                    </p>
                    {agents.filter((a) => a.id !== selectedAgent.id).length > 0 && (
                      <label className="block max-w-sm text-xs text-gray-500">
                        Split agent (optional)
                        <select
                          className="mt-1 flex h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                          value={form.__split_agent_id || ''}
                          onChange={(e) => {
                            const id = e.target.value || '';
                            const split = agents.find((a) => a.id === id) || null;
                            setForm((prev) => ({
                              ...prev,
                              __split_agent_id: id,
                              split_agent_name_code: split?.full_name || '',
                            }));
                          }}
                        >
                          <option value="">None</option>
                          {agents
                            .filter((a) => a.id !== selectedAgent.id)
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.full_name}
                              </option>
                            ))}
                        </select>
                      </label>
                    )}
                  </div>
                ) : null}
              </div>
              <span className="text-sm font-medium text-gray-500">
                Step {stepIndex + 1} of {stepLabels.length}: {currentLabel}
              </span>
            </div>
            <div className="mb-3 h-2 rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-12">
              {stepLabels.map((label, index) => {
                const active = index === stepIndex;
                const canJump = index <= furthestStepReached;
                const done = !active && canJump;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={!canJump}
                    onClick={() => goToStep(index)}
                    aria-current={active ? 'step' : undefined}
                    className={`flex items-center gap-2 rounded-md px-1 py-0.5 text-left transition-colors disabled:cursor-default disabled:opacity-100 ${
                      canJump
                        ? 'cursor-pointer hover:bg-gray-100 hover:underline'
                        : ''
                    }`}
                  >
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
                        done
                          ? 'border-primary bg-primary text-white'
                          : active
                            ? 'border-primary bg-primary/10 text-secondary'
                            : 'border-gray-300 bg-white text-gray-500'
                      }`}
                    >
                      {done ? <FiCheck className="h-3.5 w-3.5" /> : index + 1}
                    </div>
                    <span
                      className={`hidden text-xs xl:inline ${
                        active ? 'font-semibold text-secondary' : 'text-gray-500'
                      }`}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {draftSaved && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-semibold">Save &amp; Continue Later</p>
              <p className="mt-1">
                Your draft is saved. Use this link to resume anytime
                {draftSaved.emailSent ? ' (also emailed to you)' : ''}:
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="max-w-full flex-1 break-all rounded bg-white px-2 py-1 text-xs text-gray-800">
                  {draftSaved.resumeUrl}
                </code>
                <Button type="button" variant="outline" onClick={handleCopyResumeLink}>
                  <FiCopy className="mr-1 h-3.5 w-3.5" />
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDraftSaved(null)}>
                  Continue editing
                </Button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            {currentLabel === 'Agent' && (
              <Step0AgentPicker
                agents={agents}
                loading={agentsLoading}
                error={agentsError}
                selectedAgentId={form.__agent_id}
                selectedSplitAgentId={form.__split_agent_id || null}
                onSelectAgent={(agent) => applyAgentSelection(agent)}
                onSelectSplitAgent={(id) => {
                  const split = agents.find((a) => a.id === id) || null;
                  setForm((prev) => ({
                    ...prev,
                    __split_agent_id: id || '',
                    split_agent_name_code: split?.full_name || '',
                  }));
                }}
              />
            )}
            {currentLabel === 'Disclosure' && (
              <Step1Disclosure form={form} setField={setField} />
            )}
            {currentLabel === 'Personal Info' && (
              <Step2PersonalInfo form={form} setField={setField} />
            )}
            {currentLabel === 'Financial' && (
              <Step3Financial form={form} setField={setField} />
            )}
            {currentLabel === 'Insurance' && (
              <Step4Insurance
                form={form}
                setField={setField}
                setForm={setForm}
                updateBeneficiary={updateBeneficiary}
              />
            )}
            {currentLabel === 'Bank' && <Step5Bank form={form} setField={setField} />}
            {currentLabel === 'Quote' && (
              <Step6Illustration form={form} setField={setField} />
            )}
            {currentLabel === 'Health' && (
              <Step7HealthPrimary form={form} setField={setField} />
            )}
            {currentLabel === 'Spouse' && <Step8Spouse form={form} setField={setField} />}
            {currentLabel === 'Children' && (
              <Step9ChildrenRiders
                form={form}
                setField={setField}
                setForm={setForm}
                updateChild={updateChild}
              />
            )}
            {currentLabel === 'Agent Comments' && (
              <Step10AgentComments form={form} setField={setField} />
            )}
            {currentLabel === 'Review' && (
              <Step11Review
                form={form}
                onEditFirst={() => setStepIndex(0)}
                onSubmit={handleSubmit}
                submitting={submitting}
                onSaveDraft={handleSaveDraft}
                savingDraft={savingDraft}
              />
            )}

            {error && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {validationErrors.length > 0 && (
              <ul className="mt-3 list-disc space-y-1 rounded-lg border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-700">
                {validationErrors.map((err) => (
                  <li key={`${err.field}-${err.label}`}>
                    <button
                      type="button"
                      className="text-left underline hover:text-red-900"
                      onClick={() => jumpToValidationError(err)}
                    >
                      {formatSubmitValidationError(err)}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {currentLabel !== 'Review' && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={previousStep}
                  disabled={stepIndex === 0}
                >
                  Back
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    isLoading={savingDraft}
                  >
                    {savingDraft ? 'Saving…' : 'Save Draft'}
                  </Button>
                  <Button type="button" onClick={nextStep}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
